import { useState } from "react";
import { Sparkles } from "lucide-react";
import { T, withAlpha } from "../tokens";
import { Btn } from "../components/Btn";
import { usePlans, useCredits, usePaymentHistory, useBuyCredits, type Plan } from "../hooks/usePayments";

const rupees  = (paise: number) => `₹${Math.round(paise / 100).toLocaleString("en-IN")}`;
const perUnit = (p: Plan) => `${rupees(Math.round(p.amount / p.credits))} each`;
const fmtDate = (iso: string) => new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });

function PlanCard({ plan, best, busy, onBuy }: { plan: Plan; best: boolean; busy: boolean; onBuy: () => void }) {
  const [h, setH] = useState(false);
  return (
    <div onMouseEnter={() => setH(true)} onMouseLeave={() => setH(false)}
      style={{ position: "relative", flex: "1 1 160px", minWidth: 0,
        background: best ? T.tealBg : T.s1, border: `1px solid ${best ? withAlpha(T.teal, 0.4) : h ? T.b2 : T.b1}`,
        borderRadius: 12, padding: "20px 18px", transition: "border-color 0.12s ease" }}>
      {best && (
        <div style={{ position: "absolute", top: -9, left: "50%", transform: "translateX(-50%)",
          background: T.teal, color: "#04120E", fontFamily: T.mono, fontSize: 9, fontWeight: 700,
          letterSpacing: "0.06em", textTransform: "uppercase", padding: "2px 8px", borderRadius: 99 }}>Best value</div>
      )}
      <div style={{ fontFamily: T.display, fontSize: 28, fontWeight: 700, color: T.t0 }}>{plan.credits}</div>
      <div style={{ fontFamily: T.body, fontSize: 12, color: T.t2, marginBottom: 14 }}>
        {plan.credits === 1 ? "deep analysis" : "deep analyses"}
      </div>
      <div style={{ fontFamily: T.mono, fontSize: 22, fontWeight: 700, color: best ? T.tealSoft : T.t0 }}>{rupees(plan.amount)}</div>
      <div style={{ fontFamily: T.mono, fontSize: 10, color: T.t3, marginBottom: 16 }}>
        {plan.credits > 1 ? perUnit(plan) : "one-time"}
      </div>
      <Btn variant={best ? "teal" : "outline"} size="sm" onClick={onBuy} disabled={busy} style={{ width: "100%", justifyContent: "center" }}>
        {busy ? "Opening…" : "Buy"}
      </Btn>
    </div>
  );
}

export function Billing() {
  const plansQ   = usePlans();
  const creditsQ = useCredits();
  const historyQ = usePaymentHistory();
  const buy      = useBuyCredits();
  const [error, setError] = useState<string | null>(null);

  const plans   = plansQ.data?.plans ?? [];
  const enabled = plansQ.data?.enabled ?? false;
  const balance = creditsQ.data?.credits ?? 0;
  const history = historyQ.data ?? [];
  const bestId  = plans.reduce<Plan | null>((b, p) => (!b || p.credits > b.credits ? p : b), null)?.id;

  function purchase(planId: string) {
    setError(null);
    buy.mutate(planId, {
      onError: (e) => { if ((e as { message?: string })?.message !== "cancelled") setError("Payment didn't complete. Please try again."); },
    });
  }

  return (
    <div style={{ maxWidth: "960px", margin: "0 auto", width: "100%" }}>
      <div style={{ marginBottom: "24px" }}>
        <h1 style={{ fontFamily: T.display, fontSize: "26px", fontWeight: 700, color: T.t0, margin: "0 0 6px" }}>
          Credits &amp; billing
        </h1>
        <p style={{ fontFamily: T.body, fontSize: "13px", color: T.t2, margin: 0 }}>
          Credits unlock full AI deep-analysis on any match. They never expire.
        </p>
      </div>

      {/* Balance */}
      <div style={{ display: "flex", alignItems: "center", gap: 14, background: T.s1, border: `1px solid ${T.tealBdr}`,
        borderRadius: 12, padding: "20px 22px", marginBottom: 24 }}>
        <div style={{ width: 44, height: 44, borderRadius: "50%", background: T.tealBg, border: `1px solid ${T.tealBdr}`,
          display: "flex", alignItems: "center", justifyContent: "center", color: T.tealSoft, flexShrink: 0 }}>
          <Sparkles size={20}/>
        </div>
        <div>
          <div style={{ fontFamily: T.mono, fontSize: 10, color: T.t3, letterSpacing: "0.10em", textTransform: "uppercase" }}>
            Your balance
          </div>
          <div style={{ fontFamily: T.display, fontSize: 28, fontWeight: 700, color: T.t0, lineHeight: 1.1 }}>
            {creditsQ.isLoading ? "…" : balance} <span style={{ fontSize: 14, fontWeight: 500, color: T.t2 }}>credit{balance === 1 ? "" : "s"}</span>
          </div>
        </div>
      </div>

      {/* Buy packs */}
      <div style={{ fontFamily: T.mono, fontSize: 10, color: T.t3, letterSpacing: "0.12em",
        textTransform: "uppercase", marginBottom: 14 }}>Buy credits</div>
      {enabled ? (
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
          {plans.map((p) => (
            <PlanCard key={p.id} plan={p} best={p.id === bestId} busy={buy.isPending} onBuy={() => purchase(p.id)} />
          ))}
        </div>
      ) : (
        <div style={{ background: T.s1, border: `1px solid ${T.b0}`, borderRadius: 12, padding: 24, textAlign: "center" }}>
          <p style={{ fontFamily: T.body, fontSize: 13, color: T.t2, margin: 0 }}>Payments aren't enabled yet. Please check back shortly.</p>
        </div>
      )}
      {error && <p style={{ fontFamily: T.body, fontSize: 12, color: T.err, margin: "12px 0 0" }}>{error}</p>}
      <p style={{ fontFamily: T.mono, fontSize: 9.5, color: T.t3, margin: "14px 0 0" }}>
        Secure payment via Razorpay · credits never expire · Caliber never applies on your behalf
      </p>

      {/* History */}
      {history.length > 0 && (
        <div style={{ marginTop: 36 }}>
          <div style={{ fontFamily: T.mono, fontSize: 10, color: T.t3, letterSpacing: "0.12em",
            textTransform: "uppercase", marginBottom: 14 }}>Purchase history</div>
          <div style={{ background: T.s1, border: `1px solid ${T.b0}`, borderRadius: 12, overflow: "hidden" }}>
            {history.map((h, i) => (
              <div key={h.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12,
                padding: "14px 18px", borderTop: i === 0 ? "none" : `1px solid ${T.b0}` }}>
                <div>
                  <div style={{ fontFamily: T.body, fontSize: 13, fontWeight: 600, color: T.t0 }}>
                    {h.credits} deep {h.credits === 1 ? "analysis" : "analyses"}
                  </div>
                  <div style={{ fontFamily: T.mono, fontSize: 10, color: T.t3 }}>{fmtDate(h.createdAt)}</div>
                </div>
                <div style={{ fontFamily: T.mono, fontSize: 14, fontWeight: 700, color: T.t1 }}>{rupees(h.amount)}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
